import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getHostBookings, completeBooking, cancelBooking } from "apis";
import { Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { BookingDTO } from "types/DTOs/booking/booking.dto";
import { BookingStatus } from "types";

type FilterStatus = "ALL" | BookingStatus;

const filterTabs: { label: string; value: FilterStatus }[] = [
  { label: "전체", value: "ALL" },
  { label: "대기", value: BookingStatus.PENDING },
  { label: "결제완료", value: BookingStatus.PAID },
  { label: "완료", value: BookingStatus.COMPLETED },
  { label: "취소", value: BookingStatus.CANCELLED },
];

const getStatusStyle = (status: BookingStatus) => {
  switch (status) {
    case BookingStatus.PAID:
      return "bg-blue-50 text-blue-600";
    case BookingStatus.COMPLETED:
      return "bg-emerald-50 text-emerald-600";
    case BookingStatus.CANCELLED:
      return "bg-red-50 text-red-500";
    default:
      return "bg-achromatic-100 text-achromatic-500";
  }
};

const getStatusLabel = (status: BookingStatus): string => {
  switch (status) {
    case BookingStatus.PENDING: return "대기";
    case BookingStatus.PAID: return "결제완료";
    case BookingStatus.COMPLETED: return "완료";
    case BookingStatus.CANCELLED: return "취소";
    default: return status;
  }
};

export default function StudioBookings() {
  const queryClient = useQueryClient();
  const { showConfirm } = useAppModal();
  const [filter, setFilter] = useState<FilterStatus>("ALL");

  const { data: bookingsData, isLoading } = useQuery(
    ["studioHostBookings"],
    () => getHostBookings()
  );

  const completeMutation = useMutation(
    (bookingId: string) => completeBooking(bookingId),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(["studioHostBookings"]),
    }
  );

  const cancelMutation = useMutation(
    (bookingId: string) => cancelBooking(bookingId),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(["studioHostBookings"]),
    }
  );

  const bookings: BookingDTO[] = bookingsData?.data?.data ?? [];

  const filteredBookings =
    filter === "ALL"
      ? bookings
      : bookings.filter((b) => b.status === filter);

  // Sort by date descending
  const sortedBookings = [...filteredBookings].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime()
  );

  const handleComplete = async (bookingId: string) => {
    if (await showConfirm("이 예약을 완료 처리하시겠습니까?", { title: '예약 완료', confirmText: '완료', variant: 'info' })) {
      completeMutation.mutate(bookingId);
    }
  };

  const handleCancel = async (bookingId: string) => {
    if (await showConfirm("이 예약을 취소하시겠습니까?", { title: '예약 취소', confirmText: '취소', variant: 'warning' })) {
      cancelMutation.mutate(bookingId);
    }
  };

  if (isLoading) return <Loading />;

  // Count per status
  const counts = {
    ALL: bookings.length,
    [BookingStatus.PENDING]: bookings.filter(
      (b) => b.status === BookingStatus.PENDING
    ).length,
    [BookingStatus.PAID]: bookings.filter(
      (b) => b.status === BookingStatus.PAID
    ).length,
    [BookingStatus.COMPLETED]: bookings.filter(
      (b) => b.status === BookingStatus.COMPLETED
    ).length,
    [BookingStatus.CANCELLED]: bookings.filter(
      (b) => b.status === BookingStatus.CANCELLED
    ).length,
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-extrabold text-achromatic-900">
          예약 관리
        </h2>
        <p className="text-xs text-achromatic-400 mt-0.5">
          총 {bookings.length}건의 예약
        </p>
      </div>

      {/* Summary Dashboard */}
      <div className="grid grid-cols-4 gap-3 mb-5 mobile:grid-cols-2">
        {[
          { label: "대기", count: counts[BookingStatus.PENDING], emoji: "⏳", bg: "bg-amber-50", text: "text-amber-600" },
          { label: "결제완료", count: counts[BookingStatus.PAID], emoji: "💳", bg: "bg-blue-50", text: "text-blue-600" },
          { label: "완료", count: counts[BookingStatus.COMPLETED], emoji: "✅", bg: "bg-emerald-50", text: "text-emerald-600" },
          { label: "취소", count: counts[BookingStatus.CANCELLED], emoji: "❌", bg: "bg-red-50", text: "text-red-500" },
        ].map((item) => (
          <div key={item.label} className={`${item.bg} rounded-xl p-3 text-center`}>
            <span className="text-lg block">{item.emoji}</span>
            <span className={`text-lg font-extrabold ${item.text}`}>{item.count}</span>
            <span className="block text-[11px] text-achromatic-400 mt-0.5">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`whitespace-nowrap px-4 py-2 rounded-xl text-[13px] font-bold transition-all ${
              filter === tab.value
                ? "bg-primary-500 text-white"
                : "bg-white text-achromatic-500 border border-achromatic-100 hover:bg-achromatic-50"
            }`}
          >
            {tab.label}{" "}
            <span className="opacity-60">
              {counts[tab.value]}
            </span>
          </button>
        ))}
      </div>

      {/* Empty state */}
      {sortedBookings.length === 0 && (
        <div className="bg-white rounded-2xl border border-achromatic-50 p-12 text-center">
          <span className="text-4xl block mb-3">📅</span>
          <p className="text-sm font-semibold text-achromatic-800 mb-1">
            {filter === "ALL" ? "예약이 없습니다" : `${filter} 상태의 예약이 없습니다`}
          </p>
        </div>
      )}

      {/* Bookings table */}
      {sortedBookings.length > 0 && (
        <div className="bg-white rounded-2xl border border-achromatic-50 overflow-hidden">
          {/* Desktop: Table */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-achromatic-50 bg-achromatic-50/50">
                  <th className="text-left text-xs font-semibold text-achromatic-400 px-5 py-3">
                    게스트
                  </th>
                  <th className="text-left text-xs font-semibold text-achromatic-400 px-5 py-3">
                    서비스
                  </th>
                  <th className="text-left text-xs font-semibold text-achromatic-400 px-5 py-3">
                    일시
                  </th>
                  <th className="text-right text-xs font-semibold text-achromatic-400 px-5 py-3">
                    금액
                  </th>
                  <th className="text-center text-xs font-semibold text-achromatic-400 px-5 py-3">
                    상태
                  </th>
                  <th className="text-center text-xs font-semibold text-achromatic-400 px-5 py-3">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-achromatic-50">
                {sortedBookings.map((booking) => (
                  <tr
                    key={booking.id}
                    className="hover:bg-achromatic-50/30 transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-primary-50 rounded-full flex items-center justify-center text-[10px] font-bold text-primary-500 shrink-0">
                          {booking.guest?.name?.charAt(0) ?? "?"}
                        </div>
                        <span className="text-sm font-semibold text-achromatic-800">
                          {booking.guest?.name ?? "게스트"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-achromatic-700 truncate block max-w-[200px]">
                        {booking.service?.title ?? "서비스"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-achromatic-700">
                        {booking.schedule?.date ?? ""}
                      </p>
                      <p className="text-xs text-achromatic-400">
                        {booking.schedule?.startTime ?? ""} ~{" "}
                        {booking.schedule?.endTime ?? ""}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <span className="text-sm font-bold text-achromatic-900">
                        {booking.paidAmount?.toLocaleString()}원
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span
                        className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${getStatusStyle(
                          booking.status
                        )}`}
                      >
                        {getStatusLabel(booking.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {booking.status === BookingStatus.PAID && (
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleComplete(booking.id)}
                            className="px-2.5 py-1.5 text-[11px] font-bold text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                          >
                            완료
                          </button>
                          <button
                            onClick={() => handleCancel(booking.id)}
                            className="px-2.5 py-1.5 text-[11px] font-bold text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            취소
                          </button>
                        </div>
                      )}
                      {booking.status !== BookingStatus.PAID && (
                        <span className="text-xs text-achromatic-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Card list */}
          <div className="md:hidden divide-y divide-achromatic-50">
            {sortedBookings.map((booking) => (
              <div key={booking.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-primary-50 rounded-full flex items-center justify-center text-[10px] font-bold text-primary-500">
                      {booking.guest?.name?.charAt(0) ?? "?"}
                    </div>
                    <span className="text-sm font-semibold text-achromatic-800">
                      {booking.guest?.name ?? "게스트"}
                    </span>
                  </div>
                  <span
                    className={`text-[11px] font-bold px-2 py-1 rounded-md ${getStatusStyle(
                      booking.status
                    )}`}
                  >
                        {getStatusLabel(booking.status)}
                  </span>
                </div>
                <p className="text-sm text-achromatic-700 mb-1">
                  {booking.service?.title ?? "서비스"}
                </p>
                <p className="text-xs text-achromatic-400 mb-2">
                  {booking.schedule?.date ?? ""}{" "}
                  {booking.schedule?.startTime ?? ""} ~{" "}
                  {booking.schedule?.endTime ?? ""}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-achromatic-900">
                    {booking.paidAmount?.toLocaleString()}원
                  </span>
                  {booking.status === BookingStatus.PAID && (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleComplete(booking.id)}
                        className="px-3 py-1.5 text-[11px] font-bold text-emerald-600 bg-emerald-50 rounded-lg"
                      >
                        완료
                      </button>
                      <button
                        onClick={() => handleCancel(booking.id)}
                        className="px-3 py-1.5 text-[11px] font-bold text-red-500 bg-red-50 rounded-lg"
                      >
                        취소
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
